import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectConfigurationParameterById } from '!core/redux';
import { TrlLoadingIndicator, TrlPdfViewer } from '»components/atoms';
import { TrlCheckboxField } from '»components/molecules/TrlCheckboxField';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import {
  isAureaActiveClient,
  isBfacmActiveClient,
  isHemeraActiveClient,
  isStinvestActiveClient,
} from '»utils/clientsUtils';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { onboardingSlice } from '../redux';
import {
  isOnboardingContractFileLoading,
  onboardingBankAccountNumberSelector,
  onboardingContractDisclaimerSelector,
  onboardingContractFileSelector,
} from '../redux/onboarding.selectors';
import { PdfContainer, ScreenInformation } from './DocumentAcceptance.styles';

export const DocumentAcceptance = () => {
  const dispatch = useDispatch();

  const [checked, setChecked] = useState('N');
  const [isSinglePagePdf, setIsSinglePagePdf] = useState(false);
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(false);

  const contractFile = useSelector(onboardingContractFileSelector);
  const contractDisclaimer = useSelector(onboardingContractDisclaimerSelector);
  const isRequestingContract = useSelector(isOnboardingContractFileLoading);
  const bankAccountNumber = useSelector(onboardingBankAccountNumberSelector);
  const isContractPreviewEnabled =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING_CONTRACT_PREVIEW'),
    ).value === 'Y';

  const isAgreementCheckboxEnabled = isContractPreviewEnabled
    ? isSinglePagePdf || isScrollAtBottom
    : true;

  const hasDocAcceptanceCheckbox = !isStinvestActiveClient;

  const getContractFileNameId = () => {
    if (isAureaActiveClient) return 'TRLT_UI-AUREA_ONBRDNG_CNTRCT_NAME';
    if (isHemeraActiveClient) return 'TRLT_UI-HEMERA_ONBRDNG_CNTRCT_NAME';
    if (isBfacmActiveClient) return 'TRLT_UI-BFACM_ONBRDNG_CNTRCT_NAME';
    if (isStinvestActiveClient) return 'TRLT_UI-STINVEST_ONBRDNG_CNTRCT_NAME';
    return 'TRLT_UI-FINANTECH_ONBRDNG_CNTRCT_NAME';
  };

  const fileName = `${TrlFormattedMessagePlain({
    id: getContractFileNameId(),
  })}${bankAccountNumber ? ` - ${bankAccountNumber}` : ''}`;

  const handleCheckAgreement = (event) => {
    if (event.target.checked) {
      setChecked('Y');
    } else {
      setChecked('N');
    }
  };

  return (
    <PdfContainer isContractPreviewEnabled={isContractPreviewEnabled}>
      {isRequestingContract && <TrlLoadingIndicator />}
      {isContractPreviewEnabled ? (
        <TrlPdfViewer
          pdf={contractFile}
          defaultZoom={0.5}
          tools={['zoomInOut', 'download']}
          setIsScrollAtBottom={setIsScrollAtBottom}
          setIsSinglePagePdf={setIsSinglePagePdf}
          hasTransparentBackground
          fileName={fileName}
        />
      ) : (
        <ScreenInformation>
          <TrlFormattedMessagePlain
            id={
              isStinvestActiveClient
                ? 'TRLT_UI-ONBO_INFO_CONTRACT_STINVEST'
                : 'TRLT_UI-ONBOARDING_INFO_CONTRACT'
            }
          />
        </ScreenInformation>
      )}

      {hasDocAcceptanceCheckbox && (
        <TrlCheckboxField
          id="docAcceptanceCheckbox"
          checked={checked}
          handleCheckboxChange={handleCheckAgreement}
          isCheckboxDisabled={!isAgreementCheckboxEnabled}
          checkboxText={contractDisclaimer}
          hasHtmlTagsInCheckboxText={false}
        />
      )}

      <TrlFormButtons
        fixedAtBottom
        advanceButtonName="TRLT_UI-NEXT"
        isAdvanceButtonDisabled={
          hasDocAcceptanceCheckbox ? checked === 'N' : false
        }
        handleForwardButtonClick={() => {
          if (isContractPreviewEnabled) {
            try {
              const decodedData = Uint8Array.from(atob(contractFile), (char) =>
                char.charCodeAt(0),
              );

              const blob = new Blob([decodedData], { type: 'application/pdf' });

              const blobUrl = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              URL.revokeObjectURL(blobUrl);
            } catch {}
          }

          dispatch(
            onboardingSlice.actions.setOnboardingCurrentStep({
              direction: 'forward',
            }),
          );
        }}
      />
    </PdfContainer>
  );
};
